import { useState, useMemo, useEffect } from "react";
import { Card } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faChartBar,
  faChartPie,
  faMoneyBillWave,
  faArrowTrendUp,
  faArrowTrendDown,
  faTrophy,
  faSearch,
  faRocket,
  faHourglass,
  faPercent,
  faEye,
  faTriangleExclamation,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import {
  Line,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from "recharts";
import { Button } from "../../../components/ui/button";

const formatCurrency = (n) => `TZS ${Number(n).toLocaleString()}`;
const formatK = (n) =>
  n >= 1000000
    ? `${(n / 1000000).toFixed(1)}M`
    : n >= 1000
      ? `${(n / 1000).toFixed(0)}K`
      : n;

// ─── DEMO DATA ──────────────────────────────────────────────────
const generateMonthlyData = (year) => {
  const baseData = [
    {
      month: "Jan",
      sales: 3500000,
      profit: 980000,
      loss: 120000,
      orders: 45,
      products: 12,
    },
    {
      month: "Feb",
      sales: 4200000,
      profit: 1176000,
      loss: 85000,
      orders: 52,
      products: 15,
    },
    {
      month: "Mar",
      sales: 3900000,
      profit: 1092000,
      loss: 150000,
      orders: 48,
      products: 14,
    },
    {
      month: "Apr",
      sales: 4700000,
      profit: 1316000,
      loss: 60000,
      orders: 58,
      products: 18,
    },
    {
      month: "May",
      sales: 5100000,
      profit: 1428000,
      loss: 95000,
      orders: 62,
      products: 20,
    },
    {
      month: "Jun",
      sales: 4800000,
      profit: 1344000,
      loss: 180000,
      orders: 55,
      products: 17,
    },
    {
      month: "Jul",
      sales: 5300000,
      profit: 1484000,
      loss: 70000,
      orders: 65,
      products: 22,
    },
    {
      month: "Aug",
      sales: 4900000,
      profit: 1372000,
      loss: 130000,
      orders: 58,
      products: 19,
    },
    {
      month: "Sep",
      sales: 5500000,
      profit: 1540000,
      loss: 50000,
      orders: 68,
      products: 24,
    },
    {
      month: "Oct",
      sales: 6000000,
      profit: 1680000,
      loss: 110000,
      orders: 72,
      products: 26,
    },
    {
      month: "Nov",
      sales: 5800000,
      profit: 1624000,
      loss: 160000,
      orders: 70,
      products: 23,
    },
    {
      month: "Dec",
      sales: 6500000,
      profit: 1820000,
      loss: 80000,
      orders: 78,
      products: 28,
    },
  ];

  const yearMultiplier = {
    2024: 0.7,
    2025: 0.85,
    2026: 1.0,
  };

  const multiplier = yearMultiplier[year] || 1.0;
  return baseData.map((d) => ({
    ...d,
    sales: Math.round(d.sales * multiplier),
    profit: Math.round(d.profit * multiplier),
    loss: Math.round(d.loss * multiplier),
    cost: Math.round(d.sales * 0.72 * multiplier),
    orders: Math.round(d.orders * multiplier),
    products: Math.round(d.products * multiplier),
  }));
};

const generateDailyData = (year, month) => {
  const daysInMonth = new Date(year, month, 0).getDate();
  const baseSales = 1000000;
  const data = [];
  for (let i = 1; i <= daysInMonth; i++) {
    const day = i.toString().padStart(2, "0");
    const date = `${year}-${month.toString().padStart(2, "0")}-${day}`;
    const dayOfWeek = new Date(year, month - 1, i).getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const multiplier = isWeekend ? 1.4 : 1.0;
    const sales = Math.round(
      baseSales * (0.7 + Math.random() * 0.6) * multiplier,
    );
    const profit = Math.round(sales * 0.28);
    const loss = Math.round(sales * 0.02 + Math.random() * 50000);
    const orders = Math.round(10 + Math.random() * 15);
    data.push({ date, sales, profit, loss, orders });
  }
  return data;
};

// ─── Dynamic Category Data Generator ──────────────────────────
const generateCategoryData = (year, month, timeRange) => {
  const baseData = [
    {
      name: "Food",
      sales: 32000000,
      profit: 8960000,
      loss: 450000,
      growth: 12.5,
    },
    {
      name: "Detergents",
      sales: 18000000,
      profit: 5040000,
      loss: 280000,
      growth: 8.2,
    },
    {
      name: "Oils",
      sales: 15000000,
      profit: 4200000,
      loss: 320000,
      growth: 6.7,
    },
    {
      name: "Electronics",
      sales: 12000000,
      profit: 3360000,
      loss: 180000,
      growth: 15.3,
    },
    {
      name: "Clothing",
      sales: 8000000,
      profit: 2240000,
      loss: 500000,
      growth: 5.1,
    },
    {
      name: "Beverages",
      sales: 6000000,
      profit: 1680000,
      loss: 150000,
      growth: 10.8,
    },
  ];

  const yearMultiplier = {
    2024: 0.7,
    2025: 0.85,
    2026: 1.0,
  };

  const multiplier = yearMultiplier[year] || 1.0;
  const monthMultiplier = 1 + (month - 1) * 0.02;

  return baseData.map((d) => ({
    ...d,
    sales: Math.round(d.sales * multiplier * monthMultiplier),
    profit: Math.round(d.profit * multiplier * monthMultiplier),
    loss: Math.round(d.loss * multiplier * monthMultiplier),
    growth: d.growth * (0.8 + Math.random() * 0.4),
  }));
};

// ─── Dynamic Top Products Generator ────────────────────────────
const generateTopProducts = (year, month, timeRange) => {
  const baseProducts = [
    {
      name: "Rice",
      sales: 8500000,
      profit: 2380000,
      loss: 45000,
      quantity: 3400,
      turnover: "Fast",
    },
    {
      name: "Sugar",
      sales: 6200000,
      profit: 1736000,
      loss: 32000,
      quantity: 2480,
      turnover: "Fast",
    },
    {
      name: "Cooking Oil",
      sales: 5400000,
      profit: 1512000,
      loss: 68000,
      quantity: 2160,
      turnover: "Medium",
    },
    {
      name: "Wheat Flour",
      sales: 4800000,
      profit: 1344000,
      loss: 55000,
      quantity: 1920,
      turnover: "Medium",
    },
    {
      name: "Detergent Powder",
      sales: 3500000,
      profit: 980000,
      loss: 120000,
      quantity: 1400,
      turnover: "Slow",
    },
  ];

  const yearMultiplier = {
    2024: 0.7,
    2025: 0.85,
    2026: 1.0,
  };

  const multiplier = yearMultiplier[year] || 1.0;
  const monthMultiplier = 1 + (month - 1) * 0.02;

  return baseProducts.map((p) => ({
    ...p,
    sales: Math.round(p.sales * multiplier * monthMultiplier),
    profit: Math.round(p.profit * multiplier * monthMultiplier),
    loss: Math.round(p.loss * multiplier * monthMultiplier),
    quantity: Math.round(p.quantity * multiplier * monthMultiplier),
  }));
};

// ─── Dynamic Fast Moving Products ──────────────────────────────
const generateFastMoving = (year, month, timeRange) => {
  const baseData = [
    {
      name: "Rice",
      daysInStock: 7,
      salesCount: 85,
      turnover: "Fast",
      profit: 2380000,
      loss: 45000,
    },
    {
      name: "Sugar",
      daysInStock: 10,
      salesCount: 72,
      turnover: "Fast",
      profit: 1736000,
      loss: 32000,
    },
    {
      name: "Bread",
      daysInStock: 12,
      salesCount: 65,
      turnover: "Fast",
      profit: 980000,
      loss: 18000,
    },
  ];

  const yearMultiplier = {
    2024: 0.7,
    2025: 0.85,
    2026: 1.0,
  };

  const multiplier = yearMultiplier[year] || 1.0;
  const monthMultiplier = 1 + (month - 1) * 0.02;

  return baseData.map((p) => ({
    ...p,
    salesCount: Math.round(p.salesCount * multiplier * monthMultiplier),
    profit: Math.round(p.profit * multiplier * monthMultiplier),
    loss: Math.round(p.loss * multiplier * monthMultiplier),
  }));
};

// ─── Dynamic Slow Moving Products ──────────────────────────────
const generateSlowMoving = (year, month, timeRange) => {
  const baseData = [
    {
      name: "Detergent Powder",
      daysInStock: 45,
      salesCount: 12,
      turnover: "Slow",
      profit: 980000,
      loss: 120000,
    },
    {
      name: "Cooking Oil (5L)",
      daysInStock: 38,
      salesCount: 15,
      turnover: "Slow",
      profit: 560000,
      loss: 85000,
    },
    {
      name: "Toothpaste",
      daysInStock: 30,
      salesCount: 20,
      turnover: "Medium",
      profit: 420000,
      loss: 28000,
    },
  ];

  const yearMultiplier = {
    2024: 0.7,
    2025: 0.85,
    2026: 1.0,
  };

  const multiplier = yearMultiplier[year] || 1.0;
  const monthMultiplier = 1 + (month - 1) * 0.02;

  return baseData.map((p) => ({
    ...p,
    salesCount: Math.round(p.salesCount * multiplier * monthMultiplier),
    profit: Math.round(p.profit * multiplier * monthMultiplier),
    loss: Math.round(p.loss * multiplier * monthMultiplier),
    daysInStock: Math.round(p.daysInStock * (1 - (month - 1) * 0.01)),
  }));
};

// ─── Generate Insights ──────────────────────────────────────────
const generateInsights = (
  year,
  month,
  timeRange,
  categoryData,
  topProducts,
  fastMoving,
  slowMoving,
) => {
  const topFast = fastMoving
    .slice(0, 3)
    .map((p) => p.name)
    .join(", ");
  const topSlow = slowMoving
    .slice(0, 3)
    .map((p) => p.name)
    .join(", ");
  const bestCategory = categoryData.reduce((a, b) =>
    a.growth > b.growth ? a : b,
  );
  const worstCategory = categoryData.reduce((a, b) =>
    a.loss > b.loss ? a : b,
  );

  return {
    insights: [
      {
        icon: "🚀",
        color: "text-green-500",
        text: `<strong className="text-gray-900">Fast Moving:</strong> ${topFast} are the fastest moving products with turnover rates above 80%`,
      },
      {
        icon: "⏳",
        color: "text-yellow-500",
        text: `<strong className="text-gray-900">Slow Moving:</strong> ${topSlow} have been in stock for over 30 days`,
      },
      {
        icon: "📈",
        color: "text-blue-500",
        text: `<strong className="text-gray-900">Growth Opportunity:</strong> ${bestCategory.name} category shows ${bestCategory.growth.toFixed(1)}% growth, highest among all categories`,
      },
      {
        icon: "⚠️",
        color: "text-red-500",
        text: `<strong className="text-gray-900">Loss Areas:</strong> ${worstCategory.name} category has the highest loss at ${formatCurrency(worstCategory.loss)}`,
      },
    ],
    recommendations: [
      {
        icon: "✓",
        color: "text-green-500",
        text: `Increase stock levels for fast-moving products (${topFast})`,
      },
      {
        icon: "!",
        color: "text-orange-500",
        text: `Review pricing strategy for slow-moving items to improve turnover`,
      },
      {
        icon: "→",
        color: "text-blue-500",
        text: `Expand ${bestCategory.name} category to capitalize on the ${bestCategory.growth.toFixed(1)}% growth trend`,
      },
      {
        icon: "⚠",
        color: "text-red-500",
        text: `Investigate loss causes in ${worstCategory.name} category and adjust pricing or supplier strategy`,
      },
    ],
  };
};

const CHART_COLORS = [
  "#0a5cdf",
  "#10b981",
  "#df920c",
  "#e02020",
  "#8b5cf6",
  "#06b6d4",
];

// ─── PAGINATION COMPONENT ──────────────────────────────────────
const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
  onItemsPerPageChange,
  itemsPerPageOptions,
}) => {
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 pt-4 border-t border-gray-200">
      <div className="flex items-center gap-3 text-sm text-gray-600">
        <span>
          Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}{" "}
          to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
          items
        </span>
        <select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-blue-800/30 focus:border-blue-800 bg-white"
        >
          {itemsPerPageOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="h-9 px-3 border-gray-300 hover:border-blue-800"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3" />
        </Button>
        <div className="flex items-center gap-1">
          {pageNumbers.map((number) => (
            <Button
              key={number}
              variant={currentPage === number ? "default" : "outline"}
              size="sm"
              onClick={() => onPageChange(number)}
              className={`h-9 w-9 p-0 ${
                currentPage === number
                  ? "bg-blue-800 hover:bg-blue-900 text-white"
                  : "border-gray-300 hover:border-blue-800 hover:bg-blue-50"
              }`}
            >
              {number}
            </Button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="h-9 px-3 border-gray-300 hover:border-blue-800"
        >
          <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};

// ─── DROPDOWN SELECT COMPONENT ────────────────────────────────
const SelectDropdown = ({ options, value, onChange, className = "" }) => {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-800/30 focus:border-blue-800 bg-white ${className}`}
    >
      {options.map((option) => (
        <option key={option.value || option} value={option.value || option}>
          {option.label || option}
        </option>
      ))}
    </select>
  );
};

const SalesAnalysisTab = () => {
  const [selectedYear, setSelectedYear] = useState("2026");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [timeRange, setTimeRange] = useState("monthly");
  const [searchTerm, setSearchTerm] = useState("");
  const [turnoverFilter, setTurnoverFilter] = useState("all");

  // Category filters
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [categoryCurrentPage, setCategoryCurrentPage] = useState(1);
  const [categoryItemsPerPage, setCategoryItemsPerPage] = useState(6);
  const categoryItemsPerPageOptions = [6, 12, 24, 50, 100, 300];

  // Product filters
  const [productCurrentPage, setProductCurrentPage] = useState(1);
  const [productItemsPerPage, setProductItemsPerPage] = useState(5);
  const productItemsPerPageOptions = [5, 10, 15, 20, 50, 100, 300];

  const years = ["2024", "2025", "2026"];
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const timeRangeOptions = [
    { value: "monthly", label: "Monthly" },
    { value: "daily", label: "Daily" },
  ];

  // ─── Generate All Data Based on Filters ──────────────────────
  const monthlyData = useMemo(
    () => generateMonthlyData(selectedYear),
    [selectedYear],
  );
  const dailyData = useMemo(
    () => generateDailyData(selectedYear, selectedMonth),
    [selectedYear, selectedMonth],
  );
  const currentData = timeRange === "daily" ? dailyData : monthlyData;

  const categoryData = useMemo(
    () => generateCategoryData(selectedYear, selectedMonth, timeRange),
    [selectedYear, selectedMonth, timeRange],
  );

  const topProducts = useMemo(
    () => generateTopProducts(selectedYear, selectedMonth, timeRange),
    [selectedYear, selectedMonth, timeRange],
  );

  const fastMoving = useMemo(
    () => generateFastMoving(selectedYear, selectedMonth, timeRange),
    [selectedYear, selectedMonth, timeRange],
  );

  const slowMoving = useMemo(
    () => generateSlowMoving(selectedYear, selectedMonth, timeRange),
    [selectedYear, selectedMonth, timeRange],
  );

  // ─── Filter Category Data ─────────────────────────────────────
  const filteredCategoryData = useMemo(() => {
    let data = categoryData;

    // Filter by status (profit/loss)
    if (categoryFilter === "profit") {
      data = data.filter((c) => c.profit > c.loss);
    } else if (categoryFilter === "loss") {
      data = data.filter((c) => c.loss > c.profit);
    }

    // Search by category name
    if (categorySearchTerm) {
      const search = categorySearchTerm.toLowerCase();
      data = data.filter((c) => c.name.toLowerCase().includes(search));
    }

    return data;
  }, [categoryData, categoryFilter, categorySearchTerm]);

  // ─── Filter Top Products ──────────────────────────────────────
  const filteredTopProducts = useMemo(() => {
    let products = topProducts;

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      products = products.filter(
        (p) =>
          p.name.toLowerCase().includes(search) ||
          p.sales.toString().includes(search),
      );
    }

    // Turnover filter
    if (turnoverFilter !== "all") {
      products = products.filter(
        (p) => p.turnover.toLowerCase() === turnoverFilter.toLowerCase(),
      );
    }

    return products;
  }, [topProducts, searchTerm, turnoverFilter]);

  // ─── Stats ─────────────────────────────────────────────────────
  const totalSales = currentData.reduce((sum, d) => sum + d.sales, 0);
  const totalProfit = currentData.reduce((sum, d) => sum + d.profit, 0);
  const totalLoss = currentData.reduce((sum, d) => sum + (d.loss || 0), 0);
  const netProfit = totalProfit - totalLoss;

  // ─── Generate Insights ────────────────────────────────────────
  const { insights, recommendations } = useMemo(
    () =>
      generateInsights(
        selectedYear,
        selectedMonth,
        timeRange,
        categoryData,
        topProducts,
        fastMoving,
        slowMoving,
      ),
    [
      selectedYear,
      selectedMonth,
      timeRange,
      categoryData,
      topProducts,
      fastMoving,
      slowMoving,
    ],
  );

  // ─── Pagination for Category ──────────────────────────────────
  const categoryTotalItems = filteredCategoryData.length;
  const categoryTotalPages = Math.ceil(
    categoryTotalItems / categoryItemsPerPage,
  );
  const categoryStartIndex = (categoryCurrentPage - 1) * categoryItemsPerPage;
  const categoryCurrentData = filteredCategoryData.slice(
    categoryStartIndex,
    categoryStartIndex + categoryItemsPerPage,
  );

  // ─── Pagination for Products ──────────────────────────────────
  const productTotalItems = filteredTopProducts.length;
  const productTotalPages = Math.ceil(productTotalItems / productItemsPerPage);
  const productStartIndex = (productCurrentPage - 1) * productItemsPerPage;
  const productCurrentData = filteredTopProducts.slice(
    productStartIndex,
    productStartIndex + productItemsPerPage,
  );

  // ─── Reset pages when filters change ─────────────────────────
  // Use useEffect instead of useMemo for side effects
  useEffect(() => {
    setCategoryCurrentPage(1);
  }, [categoryFilter, categorySearchTerm]);

  useEffect(() => {
    setProductCurrentPage(1);
  }, [searchTerm, turnoverFilter]);

  // ─── Turnover tabs configuration ──────────────────────────────
  const turnoverTabs = [
    { id: "all", label: "All", icon: faChartBar },
    { id: "fast", label: "Fast", icon: faRocket },
    { id: "medium", label: "Medium", icon: faChartLine },
    { id: "slow", label: "Slow", icon: faHourglass },
  ];

  const turnoverCounts = {
    all: topProducts.length,
    fast: topProducts.filter((p) => p.turnover === "Fast").length,
    medium: topProducts.filter((p) => p.turnover === "Medium").length,
    slow: topProducts.filter((p) => p.turnover === "Slow").length,
  };

  // ─── Category tabs ─────────────────────────────────────────────
  const categoryTabs = [
    { id: "all", label: "All" },
    { id: "profit", label: "Profitable" },
    { id: "loss", label: "Loss Making" },
  ];

  // ─── Custom Tooltip ────────────────────────────────────────────
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-sm min-w-[180px]">
        <p className="font-semibold text-gray-900 mb-2 border-b border-gray-100 pb-2">
          {label}
        </p>
        {payload.map((p, i) => (
          <p
            key={i}
            style={{ color: p.color }}
            className="flex items-center justify-between gap-4 py-1"
          >
            <span className="flex items-center gap-2">
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: p.color }}
              ></span>
              {p.name}:
            </span>
            <span className="font-semibold">
              {typeof p.value === "number" && p.value > 10000
                ? formatCurrency(p.value)
                : p.value}
            </span>
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* ─── Header ─── */}
      <div className="flex flex-col gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Sales Analysis
          </h3>
          <p className="text-sm text-gray-600">
            Deep dive into product movement, turnover rates, and sales patterns
            including profit and loss analysis
          </p>
        </div>

        {/* ─── Filter Bar ─── */}
        <Card className="p-4 border border-gray-200 shadow-sm">
          <div className="flex flex-wrap items-center gap-3">
            {/* Time Range Select */}
            <SelectDropdown
              options={timeRangeOptions}
              value={timeRange}
              onChange={setTimeRange}
              className="w-28"
            />

            {/* Year Select */}
            <SelectDropdown
              options={years}
              value={selectedYear}
              onChange={setSelectedYear}
              className="w-24"
            />

            {/* Month Select (only for daily view) */}
            {timeRange === "daily" && (
              <SelectDropdown
                options={months.map((m) => ({
                  value: m,
                  label: monthNames[m - 1],
                }))}
                value={selectedMonth}
                onChange={(v) => setSelectedMonth(Number(v))}
                className="w-24"
              />
            )}

            <div className="flex-1"></div>

            {/* Search */}
            <div className="relative w-full sm:w-48">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 h-3.5 w-3.5"
              />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-800/30 w-full text-sm h-9"
              />
            </div>
          </div>
        </Card>
      </div>

      {/* ─── Stats Cards ─── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Card className="p-4 border border-gray-200 shadow-sm bg-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                Total Sales
              </p>
              <p className="text-2xl font-bold text-blue-600 mt-1">
                {formatCurrency(totalSales)}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faMoneyBillWave}
                className="text-blue-600 h-4 w-4"
              />
            </div>
          </div>
        </Card>

        <Card className="p-4 border border-gray-200 shadow-sm bg-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                Total Profit
              </p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {formatCurrency(totalProfit)}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faArrowTrendUp}
                className="text-green-600 h-4 w-4"
              />
            </div>
          </div>
        </Card>

        <Card className="p-4 border border-gray-200 shadow-sm bg-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                Total Loss
              </p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                {formatCurrency(totalLoss)}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faArrowTrendDown}
                className="text-red-600 h-4 w-4"
              />
            </div>
          </div>
        </Card>

        <Card className="p-4 border border-gray-200 shadow-sm bg-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                Net Profit
              </p>
              <p
                className={`text-2xl font-bold mt-1 ${netProfit >= 0 ? "text-purple-600" : "text-red-600"}`}
              >
                {formatCurrency(netProfit)}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={netProfit >= 0 ? faTrophy : faTriangleExclamation}
                className={`${netProfit >= 0 ? "text-purple-600" : "text-red-600"} h-4 w-4`}
              />
            </div>
          </div>
        </Card>

        <Card className="p-4 border border-gray-200 shadow-sm bgwhite">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                Profit/Loss Ratio
              </p>
              <p className="text-2xl font-bold text-orange-600 mt-1">
                {totalLoss > 0
                  ? `${(totalProfit / totalLoss).toFixed(1)}:1`
                  : "∞"}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faPercent}
                className="text-orange-600 h-4 w-4"
              />
            </div>
          </div>
        </Card>
      </div>

      {/* ─── Sales Trend Chart ─── */}
      <Card className="p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faChartLine}
                className="text-blue-600 h-3.5 w-3.5"
              />
            </div>
            <h4 className="font-semibold text-gray-900">
              Sales Trend with Profit & Loss
            </h4>
          </div>
          <Badge className="bg-blue-100 text-blue-700 border-blue-200">
            {timeRange === "daily"
              ? `${monthNames[selectedMonth - 1]} ${selectedYear}`
              : selectedYear}
          </Badge>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={currentData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey={timeRange === "daily" ? "date" : "month"}
              tick={{ fontSize: 11 }}
            />
            <YAxis tickFormatter={formatK} tick={{ fontSize: 11 }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar
              dataKey="sales"
              name="Sales"
              fill="#0a5cdf"
              radius={[4, 4, 0, 0]}
            />
            <Line
              type="monotone"
              dataKey="profit"
              name="Profit"
              stroke="#10b981"
              strokeWidth={2}
              dot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="loss"
              name="Loss"
              stroke="#e02020"
              strokeWidth={2}
              dot={{ r: 4 }}
              strokeDasharray="4 4"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>

      {/* ─── Category Performance ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faChartPie}
                  className="text-purple-600 h-3.5 w-3.5"
                />
              </div>
              <h4 className="font-semibold text-gray-900">
                Category Performance
              </h4>
            </div>
            <Badge className="bg-blue-100 text-blue-700 border-blue-200">
              {selectedYear}
            </Badge>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                outerRadius={80}
                dataKey="sales"
                label={({ name, percent }) =>
                  `${name} ${(percent * 100).toFixed(0)}%`
                }
                labelLine={false}
              >
                {categoryData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip formatter={(v) => formatCurrency(v)} />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        {/* ─── Category Profit & Loss with Tabs and Pagination ─── */}
        <Card className="p-4 border border-gray-200 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faChartBar}
                  className="text-indigo-600 h-3.5 w-3.5"
                />
              </div>
              <h4 className="font-semibold text-gray-900">
                Category profit & loss
              </h4>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {categoryTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setCategoryFilter(tab.id)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                    categoryFilter === tab.id
                      ? "bg-blue-800 text-white shadow-sm"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative flex-1 sm:flex-none sm:w-40">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-600 h-3 w-3"
              />
              <Input
                type="text"
                placeholder="Search category..."
                value={categorySearchTerm}
                onChange={(e) => setCategorySearchTerm(e.target.value)}
                className="pl-8 pr-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-800/30 w-full text-xs h-8"
              />
            </div>
          </div>

          <div className="space-y-2">
            {categoryCurrentData.map((cat, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200"
              >
                <span className="text-sm font-medium text-gray-700">
                  {cat.name}
                </span>
                <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                  <span className="text-xs sm:text-sm font-semibold text-green-600">
                    +{formatCurrency(cat.profit)}
                  </span>
                  <span className="text-xs sm:text-sm font-semibold text-red-600">
                    -{formatCurrency(cat.loss)}
                  </span>
                  <Badge
                    className={
                      cat.profit > cat.loss
                        ? "bg-green-600 text-white border-green-200 hover:bg-orange-500 hover:border-none text-xs"
                        : "bg-red-500 text-white border-red-200 text-xs"
                    }
                  >
                    {cat.profit > cat.loss ? "Profitable" : "Loss Making"}
                  </Badge>
                </div>
              </div>
            ))}
            {categoryCurrentData.length === 0 && (
              <div className="text-center py-4 text-gray-600 text-sm">
                No categories found
              </div>
            )}
          </div>

          {/* Category Pagination */}
          {categoryTotalItems > 0 && (
            <Pagination
              currentPage={categoryCurrentPage}
              totalPages={categoryTotalPages}
              onPageChange={setCategoryCurrentPage}
              totalItems={categoryTotalItems}
              itemsPerPage={categoryItemsPerPage}
              onItemsPerPageChange={(value) => {
                setCategoryItemsPerPage(value);
                setCategoryCurrentPage(1);
              }}
              itemsPerPageOptions={categoryItemsPerPageOptions}
            />
          )}
        </Card>
      </div>

      {/* ─── Top Performing Products with Tabs and Pagination ─── */}
      <Card className="p-4 border border-gray-200 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faTrophy}
                className="text-yellow-600 h-3.5 w-3.5"
              />
            </div>
            <h4 className="font-semibold text-gray-900">
              Top performing products
            </h4>
            <Badge className="bg-blue-100 text-blue-700 border-blue-200 ml-2">
              {selectedYear}
            </Badge>
          </div>
        </div>

        {/* Turnover Tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-wrap">
            {turnoverTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setTurnoverFilter(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                  turnoverFilter === tab.id
                    ? "bg-blue-800 text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
                }`}
              >
                <FontAwesomeIcon icon={tab.icon} className="h-3 w-3" />
                <span>{tab.label}</span>
                <span
                  className={`ml-0.5 px-1.5 py-0.5 text-xs rounded-full ${
                    turnoverFilter === tab.id
                      ? "bg-blue-100 text-blue-800"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {turnoverCounts[tab.id]}
                </span>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative flex-1 sm:flex-none sm:w-48 ml-auto">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 h-3.5 w-3.5"
            />
            <Input
              type="text"
              placeholder="Search by name or sales..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-800/30 w-full text-sm h-9"
            />
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
                <TableHead className="text-xs font-semibold text-black uppercase tracking-wider py-2.5 px-3">
                  Product Name
                </TableHead>
                <TableHead className="text-xs font-semibold text-black uppercase tracking-wider py-2.5 px-3 text-right">
                  Sales
                </TableHead>
                <TableHead className="text-xs font-semibold text-black uppercase tracking-wider py-2.5 px-3 text-right">
                  Profit
                </TableHead>
                <TableHead className="text-xs font-semibold text-black uppercase tracking-wider py-2.5 px-3 text-right">
                  Loss
                </TableHead>
                <TableHead className="text-xs font-semibold text-black uppercase tracking-wider py-2.5 px-3 text-right">
                  Net
                </TableHead>
                <TableHead className="text-xs font-semibold text-black uppercase tracking-wider py-2.5 px-3 text-center">
                  Turnover
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {productCurrentData.length > 0 ? (
                productCurrentData.map((product, idx) => {
                  const net = product.profit - product.loss;
                  return (
                    <TableRow
                      key={idx}
                      className="hover:bg-blue-50/30 transition-colors border-b border-gray-100"
                    >
                      <TableCell className="py-2.5 px-3 text-sm font-medium text-gray-900">
                        {product.name}
                      </TableCell>
                      <TableCell className="py-2.5 px-3 text-sm font-semibold text-blue-600 text-right">
                        {formatCurrency(product.sales)}
                      </TableCell>
                      <TableCell className="py-2.5 px-3 text-sm font-semibold text-green-600 text-right">
                        {formatCurrency(product.profit)}
                      </TableCell>
                      <TableCell className="py-2.5 px-3 text-sm font-semibold text-red-600 text-right">
                        {formatCurrency(product.loss)}
                      </TableCell>
                      <TableCell
                        className={`py-2.5 px-3 text-sm font-semibold text-right ${net >= 0 ? "text-green-600" : "text-red-600"}`}
                      >
                        {formatCurrency(net)}
                      </TableCell>
                      <TableCell className="py-2.5 px-3 text-center">
                        <Badge
                          className={`
                            ${
                              product.turnover === "Fast"
                                ? "bg-green-600 text-white border-green-200 hover:bg-green-700"
                                : product.turnover === "Medium"
                                  ? "bg-blue-600 text-white border-orange-200 hover:bg-blue-800"
                                  : "bg-orange-500 text-white border-red-200 hover:bg-orange-600"
                            }
                          `}
                        >
                          {product.turnover === "Fast" && (
                            <FontAwesomeIcon
                              icon={faRocket}
                              className="mr-1 h-3 w-3"
                            />
                          )}
                          {product.turnover === "Medium" && (
                            <FontAwesomeIcon
                              icon={faChartLine}
                              className="mr-1 h-3 w-3"
                            />
                          )}
                          {product.turnover === "Slow" && (
                            <FontAwesomeIcon
                              icon={faHourglass}
                              className="mr-1 h-3 w-3"
                            />
                          )}
                          {product.turnover}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-8 text-gray-600"
                  >
                    {searchTerm
                      ? "No products match your search"
                      : "No products found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Product Pagination */}
        {productTotalItems > 0 && (
          <Pagination
            currentPage={productCurrentPage}
            totalPages={productTotalPages}
            onPageChange={setProductCurrentPage}
            totalItems={productTotalItems}
            itemsPerPage={productItemsPerPage}
            onItemsPerPageChange={(value) => {
              setProductItemsPerPage(value);
              setProductCurrentPage(1);
            }}
            itemsPerPageOptions={productItemsPerPageOptions}
          />
        )}
      </Card>

      {/* ─── Insights & Recommendations ─── */}
      <Card className="p-4 border border-gray-200 bg-gradient-to-r from-blue-50 to-white">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <FontAwesomeIcon icon={faEye} className="text-blue-600 h-4 w-4" />
              <h4 className="font-semibold text-gray-900">Key Insights</h4>
              <Badge className="bg-blue-100 text-blue-700 border-blue-200 ml-2">
                {selectedYear}
              </Badge>
            </div>
            <ul className="space-y-1.5 text-sm text-gray-700">
              {insights.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className={item.color + " mt-1"}>{item.icon}</span>
                  <span dangerouslySetInnerHTML={{ __html: item.text }} />
                </li>
              ))}
            </ul>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <FontAwesomeIcon
                icon={faRocket}
                className="text-green-600 h-4 w-4"
              />
              <h4 className="font-semibold text-gray-900">Recommendations</h4>
              <Badge className="bg-green-500 text-white hover:bg-green-600 border-green-200 ml-2">
                {selectedYear}
              </Badge>
            </div>
            <ul className="space-y-1.5 text-sm text-gray-700">
              {recommendations.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className={item.color + " mt-1"}>{item.icon}</span>
                  <span dangerouslySetInnerHTML={{ __html: item.text }} />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SalesAnalysisTab;
